import { useCallback, useEffect, useState } from "react";
import { Create, useDataProvider, useNotify } from "react-admin";
import type { RaRecord } from "react-admin";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate } from "@/utils/date-format";
import { MONTH_NAMES } from "@/utils/hijri-date-utils";

import {
  formatAttendanceAttendeeLabel,
  formatAttendanceChannel,
  formatAttendanceMatchSource,
  formatCheckedInBy,
  formatOptionalText,
  formatYesNo,
} from "./miqaat-attendance-display";

const normalizeIts = (s: unknown) => String(s ?? "").trim();

type AttendanceRow = RaRecord & {
  attendeeIts?: string | null;
  attendeeName?: string | null;
  attendeeKey?: string | null;
  channel?: string | null;
  matchSource?: string | null;
  rsvpOnFile?: boolean;
  phone?: string | null;
  jamaatMohalla?: string | null;
  notes?: string | null;
  checkedInByPhone?: string | null;
  checkedInByAttendeeKey?: string | null;
  venueId?: string | null;
};

function miqaatDateLabel(miqaat: RaRecord | null): string {
  if (!miqaat) return "—";
  if (miqaat.dateMode === "PERMANENT") {
    const month = Number(miqaat.hijriMonth);
    const day = String(miqaat.hijriDay ?? "—");
    const monthLabel =
      Number.isInteger(month) && month >= 0 && month < 12
        ? MONTH_NAMES.short.en[month]
        : `month ${String(miqaat.hijriMonth ?? "—")}`;
    return `Hijri ${day} ${monthLabel}`;
  }
  if (miqaat.englishDate) {
    return formatListDate(String(miqaat.englishDate));
  }
  return "—";
}

export default function MiqaatAttendanceCreate() {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [sp] = useSearchParams();
  const miqaatId = normalizeIts(sp.get("miqaatId"));

  const [loading, setLoading] = useState(true);
  const [miqaat, setMiqaat] = useState<RaRecord | null>(null);
  const [attendanceRows, setAttendanceRows] = useState<AttendanceRow[]>([]);
  const [registeredSet, setRegisteredSet] = useState<Set<string>>(new Set());
  const [venueNameById, setVenueNameById] = useState<Record<string, string>>({});
  const [attendeeItsInput, setAttendeeItsInput] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!miqaatId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [miqaatRes, attendanceRes, rsvpRes] = await Promise.all([
        dataProvider.getOne("miqaats", { id: miqaatId }),
        dataProvider.getList("miqaatAttendance", {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "createdAt", order: "DESC" },
          filter: { miqaatId },
        }),
        callApi({
          location: "miqaats",
          method: "GET",
          id: `${miqaatId}/rsvp-attendees`,
        }),
      ]);
      setMiqaat(miqaatRes.data);
      const rows = (Array.isArray(attendanceRes.data) ? attendanceRes.data : []) as AttendanceRow[];
      setAttendanceRows(rows);

      const venueIds = [
        ...new Set(rows.map((r) => String(r.venueId ?? "").trim()).filter(Boolean)),
      ];
      if (venueIds.length > 0) {
        const venuesRes = await dataProvider.getMany("miqaatVenues", { ids: venueIds });
        const map: Record<string, string> = {};
        for (const v of venuesRes.data ?? []) {
          map[String(v.id)] = String(v.name ?? v.id);
        }
        setVenueNameById(map);
      } else {
        setVenueNameById({});
      }

      const rsvpRows = (
        ((rsvpRes.data as { rows?: unknown[] })?.rows ?? []) as Array<{
          attendeeItsNo?: string;
        }>
      ).map((r) => normalizeIts(r.attendeeItsNo));
      setRegisteredSet(new Set(rsvpRows.filter(Boolean)));
    } catch {
      notify("Could not load miqaat attendance context", { type: "warning" });
    } finally {
      setLoading(false);
    }
  }, [dataProvider, miqaatId, notify]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const addAndSave = useCallback(async () => {
    const attendeeIts = normalizeIts(attendeeItsInput);
    if (!attendeeIts || !miqaatId) return;
    setSaving(true);
    try {
      const created = await dataProvider.create("miqaatAttendance", {
        data: { miqaatId, attendeeIts },
      });
      const row = created.data as AttendanceRow;
      setAttendanceRows((prev) => [row, ...prev]);
      setAttendeeItsInput("");
      notify(
        registeredSet.has(attendeeIts) || row.rsvpOnFile
          ? "Attendance recorded (RSVP registered)"
          : "Attendance recorded (not RSVP registered)",
        { type: "success" }
      );
    } catch (e: unknown) {
      notify(e instanceof Error ? e.message : "Failed to save attendance", { type: "error" });
    } finally {
      setSaving(false);
    }
  }, [attendeeItsInput, dataProvider, miqaatId, notify, registeredSet]);

  return (
    <Create redirect={false} actions={false} title={false}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="h5" gutterBottom>
          Miqaat Attendance
        </Typography>
        {!miqaatId && <Typography color="error">`miqaatId` is required.</Typography>}
        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Miqaat context
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography>
                <strong>Name:</strong> {String(miqaat?.name ?? "—")}
              </Typography>
              <Typography>
                <strong>Date:</strong> {miqaatDateLabel(miqaat)}
              </Typography>
              <Typography>
                <strong>Meal:</strong> {String(miqaat?.mealType ?? "—")}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Add attendance by ITS
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  value={attendeeItsInput}
                  onChange={(e) => setAttendeeItsInput(e.target.value)}
                  label="Attendee ITS"
                  fullWidth
                  disabled={saving}
                  slotProps={{
                    htmlInput: {
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void addAndSave();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => void addAndSave()}
                  disabled={saving || !attendeeItsInput.trim()}
                >
                  Add
                </Button>
              </Box>
            </Paper>
            <Paper variant="outlined" sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Attendee</TableCell>
                    <TableCell>Venue</TableCell>
                    <TableCell>Channel</TableCell>
                    <TableCell>Match</TableCell>
                    <TableCell>RSVP on file</TableCell>
                    <TableCell>Checked in by</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Jamaat / Mohalla</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ color: "text.secondary" }}>
                        No attendance recorded yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    attendanceRows.map((row, idx) => {
                      const its = normalizeIts(row.attendeeIts);
                      const rsvp =
                        row.rsvpOnFile === true || (its ? registeredSet.has(its) : false);
                      const venueId = String(row.venueId ?? "").trim();
                      return (
                        <TableRow key={String(row.id ?? `${its}-${idx}`)}>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            {formatAttendanceAttendeeLabel(row)}
                          </TableCell>
                          <TableCell>
                            {venueId ? (venueNameById[venueId] ?? venueId) : "—"}
                          </TableCell>
                          <TableCell>{formatAttendanceChannel(row.channel)}</TableCell>
                          <TableCell>{formatAttendanceMatchSource(row.matchSource)}</TableCell>
                          <TableCell>{formatYesNo(rsvp)}</TableCell>
                          <TableCell>{formatCheckedInBy(row)}</TableCell>
                          <TableCell>{formatOptionalText(row.phone)}</TableCell>
                          <TableCell>{formatOptionalText(row.jamaatMohalla)}</TableCell>
                          <TableCell>{formatOptionalText(row.notes)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Paper>
          </>
        )}
      </Box>
    </Create>
  );
}
